import { getEnvironment, isProduction, isStaging } from '@upright/common/environment';
import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';
import { User } from '@upright/common/iam/users/types';
import { isDmaFreemiumUser, isManualTestUser, isTeamUser } from '@upright/common/utils';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsBrowserTabInFocus } from '../../common/generic/hooks/use-is-browser-tab-in-focus';
import {
  Appcues,
  AppcuesEventId,
  AppcuesEventPropertiesById,
  AppcuesGroupProperties,
  AppcuesSettings,
  AppcuesUserProperties,
} from './types';

declare global {
  interface Window {
    Appcues?: Appcues;
    AppcuesSettings?: AppcuesSettings;
    getCkyConsent?(): {
      // CookieYes
      categories?: {
        analytics?: boolean;
        functional?: boolean;
        advertisement?: boolean;
        necessary?: boolean;
        performance?: boolean;
      };
    };
  }
}

interface Props {
  appcuesId: string;
}

// TODO: Delete the hardcoding here and just use the env variable
export const APPCUES_ID = process.env.APPCUES_ID || '221584';

export const AppcuesInitializer = (props: Props) => {
  const { appcuesId } = props;

  useEffect(() => {
    injectAppcues(appcuesId);

    // We only want to run this effect once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export function shouldEnableAppcues() {
  if (process.env.ENABLE_APPCUES === 'true') return true;
  // TODO: Delete these and just use the env variable
  return isProduction() || isStaging();
}

// Run Appcues.debug() in the browser console to see
// that the script has been injected successfully.
function injectAppcues(appcuesId: string) {
  const appcuesElement = document.createElement('script');
  appcuesElement.async = true;
  appcuesElement.src = `//fast.appcues.com/${appcuesId}.js`;
  document.head.appendChild(appcuesElement);
}

export function useAppcues() {
  const loggedInUser = useGetLoggedInUser();
  const location = useLocation();
  const [ckyLoaded, setCkyLoaded] = useState<boolean>(false);

  document.addEventListener('cookieyes_banner_load', function (_eventData) {
    setCkyLoaded(true);
  });

  // Run only if loggedInUser changes
  useEffect(() => {
    if (loggedInUser) {
      identifyAppcuesUser(loggedInUser);
    } else {
      // Change this if we want to start using appcues on anonymous users
      resetAppcues();
    }
  }, [loggedInUser, ckyLoaded]);

  // Run on path changes
  useEffect(() => {
    updateAppcuesPageChange();
  }, [location.pathname]);
}

// Run Appcues.debug() in the browser console to see
// that the user has been identified.
function identifyAppcuesUser(user: User) {
  const environment = getEnvironment();
  const userId = isProduction(environment) ? user.id : `${user.id}-${environment}`;

  const organization = getOrganizationOfUser(user);
  const pltOrganizationId = isProduction(environment) ? `${organization?.id}` : `${organization?.id}-${environment}`;

  // Test users consume our quota too much, so let's not push them to Appcues
  if (!allowTracking() || isManualTestUser(user)) return;

  const userProperties: AppcuesUserProperties = {
    email: user.email,
    pltEmail: user.email,
    pltNameFirst: user.firstName,
    pltNameLast: user.lastName,
    pltUsername: user.username,
    pltIsTeamUser: isTeamUser(user),
    pltIsTeamTestUser: !isTeamUser(user) && user.email.endsWith('@uprightproject.com'),
    pltIsDmaFreemiumUser: isDmaFreemiumUser(user),
    pltEnvironment: environment,

    // A duplicate of the info pushed with AppcuesGroupProperties, but makes it easier
    // to debug in Appcues, because the organization name is shown under a single user
    pltOrganizationId: pltOrganizationId,
    pltOrganizationName: organization?.name,
    pltOrganizationType: organization?.type,
  };

  window.Appcues?.identify(`${userId}`, userProperties);

  if (organization) {
    const groupProperties: AppcuesGroupProperties = {
      name: organization.name,
      pltOrganizationId: pltOrganizationId,
      pltOrganizationName: organization.name,
      pltOrganizationType: organization.type,
    };
    window.Appcues?.group(pltOrganizationId, groupProperties);
  }
}

// Run Appcues.debug() in the browser console to see
// that the page changes register correctly. It should
// show an error after a changed query param, since
// we don't consider those as page changes.
function updateAppcuesPageChange() {
  if (allowTracking()) {
    window.Appcues?.page();
  }
}

function resetAppcues() {
  window.Appcues?.reset();
}

/**
 * Check if user has accepted tracking cookie consent.
 *
 * @returns true if consent is accepted, or false if not or cookieyes wasn´t loaded.
 */
function allowTracking() {
  if (window.getCkyConsent) {
    const consent = window.getCkyConsent();
    if (consent.categories?.analytics) {
      // User has accepted analytics cookies (note: Appcues could be also functional)
      return true;
    }
  }
  return false;
}

export function trackAppcuesEvent<T extends AppcuesEventId>(eventId: T, properties?: AppcuesEventPropertiesById[T]) {
  // Check tracking consent using CookieYes getCkyConsent (available only if cookieyes banner is displayed)
  if (allowTracking()) {
    window.Appcues?.track(eventId, properties);
  }
}

/**
 * A hook to get an Appcues event tracker that fires only once during the lifetime of the component, and only
 * if the browser tab is in focus.
 */
export function useGetSingleFireAppcuesTracker<T extends AppcuesEventId>() {
  // We don't want to track events from tabs that are not in view of the user (for example, opened in a
  // new tab but not yet viewed). In such cases, we'll track the event as soon as the user focuses
  // the tab.
  const isTheTabActive = useIsBrowserTabInFocus();

  // To avoid spamming Appcues on every render, we track the event only once during the lifetime of the component.
  // We use a ref instead of a useEffect hook so that we can return the tracking function from this hook
  // and use it in contexts that don't allow hooks, such as click handlers of buttons.
  const hasTracked = useRef(false);

  function trackEventOnce(eventId: T, properties?: AppcuesEventPropertiesById[T]) {
    if (!isTheTabActive || hasTracked.current) return;
    trackAppcuesEvent(eventId, properties);
    hasTracked.current = true;
  }
  return trackEventOnce;
}
